import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabase';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sürükle-bırak satır bileşeni
function SortableRow({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return <tr ref={setNodeRef} style={style} {...attributes}>{children(listeners)}</tr>;
}

// SQL — Supabase'de çalıştırılacak:
// ALTER TABLE depo ADD COLUMN IF NOT EXISTS irsaliye_no varchar;
// ALTER TABLE depo ADD COLUMN IF NOT EXISTS gonderici_firma varchar;
// ALTER TABLE kullanicilar ADD COLUMN IF NOT EXISTS depo_sehir varchar DEFAULT 'İstanbul';
// ALTER TABLE depo ADD COLUMN IF NOT EXISTS depo_sehir varchar DEFAULT 'İstanbul';
// UPDATE kullanicilar SET depo_sehir = 'İstanbul' WHERE rol = 'depo';
// ALTER TABLE depo ADD COLUMN IF NOT EXISTS teslim_kodu varchar(4);
// ALTER TABLE depo ADD COLUMN IF NOT EXISTS on_ucret_var boolean DEFAULT false;
// ALTER TABLE depo ADD COLUMN IF NOT EXISTS on_ucret_turu varchar;
// ALTER TABLE depo ADD COLUMN IF NOT EXISTS on_ucret_aciklama varchar;
// ALTER TABLE depo ADD COLUMN IF NOT EXISTS on_ucret_tutari numeric;
// ALTER TABLE depo ADD COLUMN IF NOT EXISTS on_ucret_para_birimi varchar DEFAULT 'TL';

const ULKELER = [
  // Sık kullanılanlar
  'KKTC', 'Montenegro', 'Türkiye',
  '---',
  // Alfabetik tam liste
  'Afganistan', 'Almanya', 'Amerika Birleşik Devletleri', 'Arjantin', 'Arnavutluk',
  'Avustralya', 'Avusturya', 'Azerbaycan', 'Bahreyn', 'Belçika', 'Beyaz Rusya',
  'Bosna Hersek', 'Brezilya', 'Bulgaristan', 'Cezayir', 'Çek Cumhuriyeti', 'Çin',
  'Danimarka', 'Mısır', 'Emirati Arap Emirlikleri', 'Ermenistan', 'Estonya',
  'Etiyopya', 'Fas', 'Finlandiya', 'Fransa', 'Gürcistan', 'Hollanda', 'Hırvatistan',
  'Hindistan', 'Irak', 'İngiltere', 'İran', 'İrlanda', 'İspanya', 'İsrail',
  'İsveç', 'İsviçre', 'İtalya', 'İzlanda', 'Japonya', 'Kanada', 'Katar',
  'Kazakistan', 'Kenya', 'Kırgızistan', 'Kolombiya', 'Kosova', 'Kuveyt',
  'Kuzey Makedonya', 'Letonya', 'Lihtenştayn', 'Litvanya', 'Lübnan', 'Lüksemburg',
  'Macaristan', 'Malezya', 'Malta', 'Meksika', 'Moldova', 'Moğolistan',
  'Nijerya', 'Norveç', 'Özbekistan', 'Pakistan', 'Polonya', 'Portekiz',
  'Romanya', 'Rusya', 'Suudi Arabistan', 'Sırbistan', 'Singapur', 'Slovakya',
  'Slovenya', 'Sudan', 'Suriye', 'Tacikistan', 'Tayland', 'Tunus',
  'Türkmenistan', 'Ukrayna', 'Umman', 'Ürdün', 'Venezuela', 'Vietnam',
  'Yemen', 'Yunanistan', 'Yeni Zelanda'
];

// Türkçe karakter dönüştürücü (jsPDF için)
const tr = (s) => (s || '').replace(/İ/g,'I').replace(/ı/g,'i').replace(/Ş/g,'S').replace(/ş/g,'s')
  .replace(/Ğ/g,'G').replace(/ğ/g,'g').replace(/Ü/g,'U').replace(/ü/g,'u')
  .replace(/Ö/g,'O').replace(/ö/g,'o').replace(/Ç/g,'C').replace(/ç/g,'c')
  .replace(/→/g,'->');

// 0532 123 45 67
const formatTelefon = (val) => {
  const temiz = val.replace(/\D/g, '').slice(0, 11);
  let r = temiz.slice(0, 4);
  if (temiz.length > 4) r += ' ' + temiz.slice(4, 7);
  if (temiz.length > 7) r += ' ' + temiz.slice(7, 9);
  if (temiz.length > 9) r += ' ' + temiz.slice(9, 11);
  return r;
};

// Kargo takip kodu üret: BYR-2847-XK9M
const takipKoduUret = () => {
  const sayi = String(Math.floor(1000 + Math.random() * 9000));
  const harfler = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const harf = Array.from({ length: 4 }, () => harfler[Math.floor(Math.random() * harfler.length)]).join('');
  return `BYR-${sayi}-${harf}`;
};

const teslimKoduUret = async () => {
  const { data } = await supabase.from('depo').select('teslim_kodu').not('teslim_kodu', 'is', null);
  const mevcutlar = new Set((data || []).map(d => d.teslim_kodu));
  let kod;
  do { kod = String(Math.floor(1000 + Math.random() * 9000)); } while (mevcutlar.has(kod));
  return kod;
};


// ── Sayfalama çubuğu ────────────────────────────────────────────
function PaginasyonBar({ toplamKayit, sayfaNo, toplamSayfa, sayfaBasinaKayit, onSayfaBasinaKayitDegis, onSayfaDegis, solBilgi, mobil }) {
  const baslangic = sayfaBasinaKayit === 0 ? 1 : (sayfaNo - 1) * sayfaBasinaKayit + 1;
  const bitis     = sayfaBasinaKayit === 0 ? toplamKayit : Math.min(sayfaNo * sayfaBasinaKayit, toplamKayit);
  const btnSt = (disabled) => ({
    padding: '3px 10px', border: '1px solid #ddd', borderRadius: '5px',
    background: disabled ? '#fafafa' : '#fff', color: disabled ? '#ccc' : '#333',
    cursor: disabled ? 'default' : 'pointer', fontSize: '12px',
  });
  return (
    <div style={{ padding: mobil ? '10px 14px' : '10px 20px', borderTop: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', fontSize: '12px', color: '#888' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        {solBilgi}
        {solBilgi && <span style={{ color: '#ddd' }}>|</span>}
        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#666' }}>
          Sayfa başına:
          <select
            value={sayfaBasinaKayit}
            onChange={e => onSayfaBasinaKayitDegis(Number(e.target.value))}
            style={{ padding: '2px 6px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', marginLeft: '4px' }}
          >
            {[10, 25, 50, 100].map(v => <option key={v} value={v}>{v}</option>)}
            <option value={0}>Tümü</option>
          </select>
        </label>
      </div>
      {sayfaBasinaKayit !== 0 && toplamKayit > 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: '#666' }}>Toplam {toplamKayit} kayıt ({baslangic}–{bitis})</span>
          <button onClick={() => onSayfaDegis(s => Math.max(1, s - 1))} disabled={sayfaNo <= 1} style={btnSt(sayfaNo <= 1)}>← Önceki</button>
          <span style={{ fontWeight: '600', color: '#0D3B7A', whiteSpace: 'nowrap' }}>Sayfa {sayfaNo} / {toplamSayfa}</span>
          <button onClick={() => onSayfaDegis(s => Math.min(toplamSayfa, s + 1))} disabled={sayfaNo >= toplamSayfa} style={btnSt(sayfaNo >= toplamSayfa)}>Sonraki →</button>
        </div>
      ) : (
        <span style={{ color: '#666' }}>Toplam {toplamKayit} kayıt gösteriliyor</span>
      )}
    </div>
  );
}

function Depo({ onCikis, kullanici: kullaniciProp } = {}) {
  const [mallar, setMallar] = useState([]);
  const [musteriler, setMusteriler] = useState([]);
  const [seferler, setSeferler] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [girisAcik, setGirisAcik] = useState(false);
  const [tiraAtaModal, setTiraAtaModal] = useState(null);
  const [secilenSeferId, setSecilenSeferId] = useState('');
  const [kargoAciklama, setKargoAciklama] = useState('');
  const [musteriArama, setMusteriArama] = useState('');
  const [musteriDropAcik, setMusteriDropAcik] = useState(false);
  const musteriDropRef = useRef(null);
  const [yeniMusteriModal, setYeniMusteriModal] = useState(false);
  const [duzenleModal, setDuzenleModal] = useState(null);
  const [secilenMallar, setSecilenMallar] = useState(new Set());
  const [topluSeferId, setTopluSeferId] = useState('');
  const [kopyalandi, setKopyalandi] = useState(null);
  const [ulkeFiltre, setUlkeFiltre] = useState('Tümü');
  const [mobil, setMobil] = useState(window.innerWidth <= 768);
  const [aktifDepoSekme, setAktifDepoSekme] = useState('depo');
  const [depoSehirFiltre, setDepoSehirFiltre] = useState('Tüm Depolar');
  const [acikKodSatiri, setAcikKodSatiri] = useState(null);
  // DnD sıralama
  const [depoSiraliIds, setDepoSiraliIds] = useState([]);
  const [atananSiraliIds, setAtananSiraliIds] = useState([]);
  // Sefere Atanan filtresi
  const [aktifSeferlerAtanan, setAktifSeferlerAtanan] = useState([]);
  const [secilenSeferIdAtanan, setSecilenSeferIdAtanan] = useState('');
  const [atananSeferMallari, setAtananSeferMallari] = useState([]);
  const [atananYukleniyor, setAtananYukleniyor] = useState(false);
  // Sayfalama
  const [depoSayfaBasinaKayit, setDepoSayfaBasinaKayit] = useState(25);
  const [depoSayfaNo, setDepoSayfaNo] = useState(1);
  const [atananSayfaBasinaKayit, setAtananSayfaBasinaKayit] = useState(25);
  const [atananSayfaNo, setAtananSayfaNo] = useState(1);
  // Arama filtresi
  const [aramaMusteri, setAramaMusteri] = useState('');
  const [aramaFaturaNo, setAramaFaturaNo] = useState('');
  const [aramaGonderici, setAramaGonderici] = useState('');

  // Ekran boyutu takibi
  useEffect(() => {
    const kontrol = () => setMobil(window.innerWidth <= 768);
    window.addEventListener('resize', kontrol);
    return () => window.removeEventListener('resize', kontrol);
  }, []);

  useEffect(() => {
    if (!musteriDropAcik) return;
    const disariyaTiklandi = (e) => {
      if (musteriDropRef.current && !musteriDropRef.current.contains(e.target)) setMusteriDropAcik(false);
    };
    document.addEventListener('mousedown', disariyaTiklandi);
    return () => document.removeEventListener('mousedown', disariyaTiklandi);
  }, [musteriDropAcik]);

  const bosYeniMal = {
    musteri_id: '', musteri_ad: '', urun: '', miktar: '',
    hedef_ulke: 'KKTC', fatura_no: '', gonderici_firma: '', notlar: '', durum: 'Bekliyor',
    on_ucret_var: false, on_ucret_turu: 'İç Nakliye', on_ucret_aciklama: '', on_ucret_tutari: '', on_ucret_para_birimi: 'TL',
  };
  const [yeniMal, setYeniMal] = useState(bosYeniMal);

  const [yeniMusteri, setYeniMusteri] = useState({
    ad: '', tip: 'Bireysel', telefon: '', email: '', sehir: '', durum: 'Aktif'
  });

  useEffect(() => {
    mallariGetir();
    musterileriGetir();
    seferleriGetir();
    aktifSeferleriGetir();
  }, []);

  const mallariGetir = async (sehirParam) => {
    setYukleniyor(true);
    const sehirFiltre = sehirParam !== undefined ? sehirParam : depoSehirFiltre;
    let query = supabase
      .from('depo')
      .select('*, musteriler(id, ad, telefon, sehir)')
      .order('created_at', { ascending: false });
    // Depo rolü → sadece kendi şehrini görür
    if (kullaniciRol === 'depo') {
      query = query.eq('depo_sehir', depoSehir);
    // Yönetici/operasyon → dropdown filtresine göre
    } else if (yetkiliRol && sehirFiltre && sehirFiltre !== 'Tüm Depolar') {
      query = query.eq('depo_sehir', sehirFiltre);
    }
    const { data } = await query;
    if (data) setMallar(data);
    setYukleniyor(false);
  };

  const musterileriGetir = async () => {
    const { data } = await supabase.from('musteriler').select('id, ad, tip').order('ad');
    if (data) setMusteriler(data);
  };

  const seferleriGetir = async () => {
    const { data } = await supabase
      .from('seferler')
      .select('id, sefer_no, guzergah, durum')
      .eq('durum', 'Yükleme Bekliyor')
      .order('created_at', { ascending: false });
    if (data) setSeferler(data);
  };

  const aktifSeferleriGetir = async () => {
    const { data } = await supabase
      .from('seferler')
      .select('id, sefer_no, guzergah, durum')
      .neq('durum', 'Teslim Edildi')
      .order('created_at', { ascending: false });
    if (data) setAktifSeferlerAtanan(data);
  };

  const atananMallariGetir = async (seferId) => {
    if (!seferId) { setAtananSeferMallari([]); setAtananSiraliIds([]); return; }
    setAtananYukleniyor(true);
    const { data } = await supabase
      .from('depo')
      .select('*, musteriler(id, ad, telefon, sehir)')
      .eq('sefer_id', seferId)
      .neq('durum', 'Teslim Edildi');
    const list = data || [];
    setAtananSeferMallari(list);
    setAtananSiraliIds(list.map(m => m.id));
    setAtananYukleniyor(false);
  };

  const depoMallar    = mallar.filter(m => m.durum === 'Bekliyor' || m.durum === 'Planlamada');
  const atananMallar  = mallar.filter(m => m.sefer_id && m.durum !== 'Teslim Edildi');
  const mevcutUlkeler = [...new Set(depoMallar.map(m => m.hedef_ulke).filter(Boolean))].sort();
  const gorunurMallar = ulkeFiltre === 'Tümü'
    ? depoMallar
    : depoMallar.filter(m => m.hedef_ulke === ulkeFiltre);

  const filtrelenmisDepoMallar = gorunurMallar.filter(m => {
    if (aramaMusteri && !(m.musteriler?.ad || '').toLowerCase().includes(aramaMusteri.toLowerCase())) return false;
    if (aramaFaturaNo && !(m.fatura_no || '').toLowerCase().includes(aramaFaturaNo.toLowerCase())) return false;
    if (aramaGonderici && !(m.gonderici_firma || '').toLowerCase().includes(aramaGonderici.toLowerCase())) return false;
    return true;
  });

  // Sayfa sıfırlama
  useEffect(() => { setDepoSayfaNo(1); }, [ulkeFiltre, aktifDepoSekme]); // eslint-disable-line
  useEffect(() => { setAtananSayfaNo(1); }, [secilenSeferIdAtanan]); // eslint-disable-line
  useEffect(() => { setDepoSayfaNo(1); setAtananSayfaNo(1); }, [aramaMusteri, aramaFaturaNo, aramaGonderici]); // eslint-disable-line

  // DnD: filtrelenmisDepoMallar değişince sıralamayı senkronize et
  useEffect(() => {
    setDepoSiraliIds(prev => {
      const newIds = filtrelenmisDepoMallar.map(m => m.id);
      const prevSet = new Set(prev);
      if (newIds.length !== prev.length || newIds.some(id => !prevSet.has(id))) return newIds;
      return prev;
    });
  }, [mallar, ulkeFiltre, aramaMusteri, aramaFaturaNo, aramaGonderici]); // eslint-disable-line

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );

  // Atanan tab arama filtresi
  const filtrelenmisAtananMallar = atananSeferMallari.filter(m => {
    if (aramaMusteri && !(m.musteriler?.ad || '').toLowerCase().includes(aramaMusteri.toLowerCase())) return false;
    if (aramaFaturaNo && !(m.fatura_no || '').toLowerCase().includes(aramaFaturaNo.toLowerCase())) return false;
    if (aramaGonderici && !(m.gonderici_firma || '').toLowerCase().includes(aramaGonderici.toLowerCase())) return false;
    return true;
  });
  const filtrelenmisAtananIds = atananSiraliIds.filter(id => filtrelenmisAtananMallar.some(m => m.id === id));

  // Sayfalanmış ID listeleri
  const depoToplamKayit = depoSiraliIds.length;
  const depoToplamSayfa = depoSayfaBasinaKayit === 0 ? 1 : Math.ceil(depoToplamKayit / depoSayfaBasinaKayit);
  const depoSayfaliIds = depoSayfaBasinaKayit === 0
    ? depoSiraliIds
    : depoSiraliIds.slice((depoSayfaNo - 1) * depoSayfaBasinaKayit, depoSayfaNo * depoSayfaBasinaKayit);

  const atananToplamKayit = filtrelenmisAtananIds.length;
  const atananToplamSayfa = atananSayfaBasinaKayit === 0 ? 1 : Math.ceil(atananToplamKayit / atananSayfaBasinaKayit);
  const atananSayfaliIds = atananSayfaBasinaKayit === 0
    ? filtrelenmisAtananIds
    : filtrelenmisAtananIds.slice((atananSayfaNo - 1) * atananSayfaBasinaKayit, atananSayfaNo * atananSayfaBasinaKayit);

  const filtrelenmisMusteriler = musteriler.filter(m =>
    m.ad.toLowerCase().includes(musteriArama.toLowerCase())
  );

  const musteriSec = (m) => {
    setYeniMal({ ...yeniMal, musteri_id: m.id, musteri_ad: m.ad });
    setMusteriArama(m.ad);
    setMusteriDropAcik(false);
  };

  // ── Barkod No Üret ──────────────────────────────────────────
  const barkodNoUret = async () => {
    const yil = new Date().getFullYear();
    const prefix = `BYR-${yil}-`;
    const { data } = await supabase
      .from('depo')
      .select('barkod_no')
      .like('barkod_no', `${prefix}%`);
    let maxNo = 0;
    (data || []).forEach(d => {
      if (d.barkod_no) {
        const parca = d.barkod_no.split('-');
        const no = parseInt(parca[parca.length - 1]);
        if (!isNaN(no) && no > maxNo) maxNo = no;
      }
    });
    return `${prefix}${String(maxNo + 1).padStart(5, '0')}`;
  };

  const koduKopyala = (kod) => {
    if (!kod) return;
    navigator.clipboard.writeText(kod).then(() => {
      setKopyalandi(kod);
      setTimeout(() => setKopyalandi(null), 2000);
    });
  };

  const malEkle = async () => {
    if (!yeniMal.urun) return alert('Ürün adı zorunlu!');
    const barkod_no = await barkodNoUret();
    const takip_kodu = takipKoduUret();
    const teslim_kodu = await teslimKoduUret();
    const kayit = { ...yeniMal, barkod_no, takip_kodu, teslim_kodu, kod_gecerli: true, depo_sehir: depoSehir };
    delete kayit.musteri_ad;
    if (!kayit.musteri_id) delete kayit.musteri_id;
    if (!kayit.on_ucret_var) {
      kayit.on_ucret_turu = null;
      kayit.on_ucret_aciklama = null;
      kayit.on_ucret_tutari = null;
      kayit.on_ucret_para_birimi = null;
    } else {
      kayit.on_ucret_tutari = parseFloat(kayit.on_ucret_tutari) || null;
      if (kayit.on_ucret_turu !== 'Diğer') kayit.on_ucret_aciklama = null;
    }
    const { error } = await supabase.from('depo').insert([kayit]);
    if (error) { alert('Hata: ' + error.message); }
    else {
      setGirisAcik(false);
      setYeniMal(bosYeniMal);
      setMusteriArama('');
      mallariGetir();
    }
  };

  // ── Etiket PDF İndir ────────────────────────────────────────
  const etiketIndir = async (mal) => {
    const { jsPDF } = await import('jspdf');

    // Koli sayısını miktar alanından çıkar
    const sayiMatch = (mal.miktar || '').match(/\d+/);
    const koliSayisi = sayiMatch ? parseInt(sayiMatch[0]) : 1;

    const musteri   = mal.musteriler || {};
    const takipKodu = mal.takip_kodu || null;
    const telefon   = musteri.telefon || null;

    // Sayfa boyutu: 60x40mm
    const W = 60, H = 40;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [W, H] });

    for (let i = 1; i <= koliSayisi; i++) {
      if (i > 1) doc.addPage([W, H]);

      // ── ÜST: Koyu şerit ──
      doc.setFillColor(13, 59, 122);
      doc.rect(0, 0, W, 9, 'F');

      // "BYR SWORD LOJİSTİK" — ortada, kalın, beyaz
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('BYR SWORD LOJISTIK', W / 2, 5.5, { align: 'center' });

      // "byrsword.com" — küçük, soluk
      doc.setFontSize(4.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(160, 195, 240);
      doc.text('byrsword.com', W / 2, 8, { align: 'center' });

      // ── ORTA: Bilgi satırları ──
      const satir = (etiket, deger, y) => {
        doc.setFontSize(5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(110, 110, 110);
        doc.text(etiket, 3, y);
        doc.setFontSize(6);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(15, 15, 15);
        const maxChar = 26;
        const metin = tr(deger || '—');
        doc.text(metin.length > maxChar ? metin.slice(0, maxChar) + '...' : metin, 18, y);
      };

      satir('Musteri :', musteri?.ad,   13);
      satir('Tel     :', telefon,       17.5);
      satir('Urun    :', mal.urun,      22);
      satir('Koli    :', `${i}/${koliSayisi}`, 26.5);
      satir('Hedef   :', mal.hedef_ulke, 31);

      // ── Ayırıcı çizgi ──
      doc.setDrawColor(210, 210, 210);
      doc.line(3, 33, W - 3, 33);

      // ── ALT: Takip Kodu ──
      doc.setFontSize(4.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(130, 130, 130);
      doc.text('TAKIP KODU', W / 2, 35.8, { align: 'center' });

      if (takipKodu) {
        doc.setFontSize(9.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(13, 59, 122);
        doc.text(tr(takipKodu), W / 2, 39, { align: 'center' });
      } else {
        doc.setFontSize(5.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(190, 190, 190);
        doc.text('Kod henuz olusturulmadi', W / 2, 38.5, { align: 'center' });
      }
    }

    const dosyaAdi = mal.barkod_no || `etiket_${mal.id}`;
    doc.save(`Etiket_${dosyaAdi}.pdf`);
  };

  const depoListesiPDF = async () => {
    const { jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const bugunStr = new Date().toLocaleDateString('tr-TR');
    const L = 10, R = pageW - 10;

    const veri = aktifDepoSekme === 'depo' ? gorunurMallar : atananMallar;
    const sekmeAdi = aktifDepoSekme === 'depo' ? 'Depo Listesi' : 'Sefere Atanan Mallar';
    const depoAdi = yetkiliRol
      ? (depoSehirFiltre === 'Tüm Depolar' ? 'Tum Depolar' : tr(depoSehirFiltre) + ' Deposu')
      : tr(depoSehir) + ' Deposu';

    const tarihStr = (d) => d ? new Date(d).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

    // Başlık
    doc.setFillColor(13, 59, 122);
    doc.rect(0, 0, pageW, 22, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('BYR SWORD LOJISTIK', pageW / 2, 10, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(180, 205, 240);
    doc.text(tr(sekmeAdi).toUpperCase(), pageW / 2, 17, { align: 'center' });
    doc.setFontSize(8);
    doc.setTextColor(220, 230, 245);
    doc.text(`Tarih: ${bugunStr}`, R, 9, { align: 'right' });
    doc.text(`Depo: ${depoAdi}`, R, 15, { align: 'right' });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(180, 220, 255);
    doc.text(`Toplam: ${veri.length} kalem`, L, 9, { align: 'left' });

    const kolonlar = ['#', 'Musteri', 'Gonderici Firma', 'Urun', 'Miktar', 'Hedef', 'Fatura No', 'Depo', 'Kayit Tarihi'];

    const satirOlustur = (mal, idx) => [
      idx,
      tr(mal.musteriler?.ad) || '-',
      tr(mal.gonderici_firma) || '-',
      tr(mal.urun) || '-',
      tr(mal.miktar) || '-',
      tr(mal.hedef_ulke) || '-',
      tr(mal.fatura_no) || '-',
      tr(mal.depo_sehir) || '-',
      tarihStr(mal.created_at),
    ];

    const faturali = veri.filter(m => m.fatura_no && m.fatura_no.trim());
    const faturasiz = veri.filter(m => !m.fatura_no || !m.fatura_no.trim());
    const eksik = faturasiz.filter(m => !m.urun?.trim() || !m.miktar?.trim() || !m.hedef_ulke?.trim());
    const sadeceFaturasiz = faturasiz.filter(m => m.urun?.trim() && m.miktar?.trim() && m.hedef_ulke?.trim());

    const sirala = (arr) => [...arr].sort((a, b) => tr(a.musteriler?.ad || '').localeCompare(tr(b.musteriler?.ad || '')));

    let startY = 28;
    const tabloStil = {
      styles: { fontSize: 7.5, cellPadding: 2.5, textColor: [30, 30, 30], lineColor: [220, 225, 235], lineWidth: 0.2 },
      headStyles: { fillColor: [13, 59, 122], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7 },
      alternateRowStyles: { fillColor: [248, 250, 254] },
      columnStyles: { 0: { cellWidth: 8, halign: 'center' }, 10: { cellWidth: 28 } },
      margin: { left: L, right: L },
    };

    // Faturalı kayıtlar
    if (faturali.length > 0) {
      let idx = 1;
      autoTable(doc, {
        startY,
        head: [kolonlar],
        body: sirala(faturali).map(m => satirOlustur(m, idx++)),
        ...tabloStil,
      });
      startY = doc.lastAutoTable.finalY + 6;
    }

    // Faturasız kayıtlar
    if (sadeceFaturasiz.length > 0) {
      if (startY > pageH - 30) { doc.addPage(); startY = 14; }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(200, 80, 0);
      doc.text(`FATURASI OLMAYAN KAYITLAR (${sadeceFaturasiz.length})`, L, startY);
      startY += 3;
      let idx = 1;
      autoTable(doc, {
        startY,
        head: [kolonlar],
        body: sirala(sadeceFaturasiz).map(m => satirOlustur(m, idx++)),
        ...tabloStil,
        headStyles: { ...tabloStil.headStyles, fillColor: [200, 80, 0] },
      });
      startY = doc.lastAutoTable.finalY + 6;
    }

    // Eksik bilgili kayıtlar
    if (eksik.length > 0) {
      if (startY > pageH - 30) { doc.addPage(); startY = 14; }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(180, 40, 40);
      doc.text(`EKSIK BILGILI KAYITLAR (${eksik.length})`, L, startY);
      startY += 3;
      let idx = 1;
      autoTable(doc, {
        startY,
        head: [kolonlar],
        body: sirala(eksik).map(m => satirOlustur(m, idx++)),
        ...tabloStil,
        headStyles: { ...tabloStil.headStyles, fillColor: [180, 40, 40] },
      });
      startY = doc.lastAutoTable.finalY + 6;
    }

    // Footer özet
    const sonY = Math.min(startY + 4, pageH - 12);
    doc.setFillColor(236, 242, 252);
    doc.setDrawColor(210, 220, 235);
    doc.rect(L, sonY, R - L, 9, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(13, 59, 122);
    doc.text(`Toplam: ${veri.length} kalem  |  Faturali: ${faturali.length}  |  Faturasiz: ${faturasiz.length}`, L + 4, sonY + 6);

    // Tüm sayfalarda alt çizgi + sayfa numarası
    const toplamSayfa = doc.internal.getNumberOfPages();
    for (let p = 1; p <= toplamSayfa; p++) {
      doc.setPage(p);
      doc.setFillColor(13, 59, 122);
      doc.rect(0, pageH - 6, pageW, 6, 'F');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      doc.setTextColor(180, 205, 240);
      doc.text(
        `BYR SWORD LOJISTIK  |  Bu belge otomatik olusturulmustur.  |  Sayfa ${p} / ${toplamSayfa}`,
        pageW / 2, pageH - 2, { align: 'center' }
      );
    }

    doc.save(`Depo_Listesi_${bugunStr.replace(/\./g, '-')}.pdf`);
  };

  const yeniMusteriKaydet = async () => {
    if (!yeniMusteri.ad) return alert('Müşteri adı zorunlu!');
    const { data, error } = await supabase.from('musteriler').insert([yeniMusteri]).select().single();
    if (error) { alert('Hata: ' + error.message); }
    else {
      setYeniMusteriModal(false);
      setYeniMusteri({ ad: '', tip: 'Bireysel', telefon: '', email: '', sehir: '', durum: 'Aktif' });
      musterileriGetir();
      setYeniMal(prev => ({ ...prev, musteri_id: data.id, musteri_ad: data.ad }));
      setMusteriArama(data.ad);
    }
  };

  // Seferdeki tüm mallar teslim edildiyse müşteri takip kodlarını geçersizleştir
  const takipKodlariniGecersizlestir = async (seferId) => {
    const { data: seferMallari } = await supabase
      .from('depo')
      .select('durum')
      .eq('sefer_id', seferId);
    const tumTeslim = seferMallari && seferMallari.length > 0 &&
      seferMallari.every(m => m.durum === 'Teslim Edildi');
    if (!tumTeslim) return;
    const { data: seferMusteriler } = await supabase
      .from('sefer_musteriler')
      .select('musteri_id')
      .eq('sefer_id', seferId);
    if (!seferMusteriler || seferMusteriler.length === 0) return;
    const musteriIds = seferMusteriler.map(sm => sm.musteri_id).filter(Boolean);
    if (musteriIds.length === 0) return;
    await supabase
      .from('musteriler')
      .update({ kod_gecerli: false })
      .in('id', musteriIds);
  };

  const malGuncelle = async () => {
    if (!duzenleModal.urun) return alert('Ürün adı zorunlu!');
    const { id, musteriler: _, created_at, ...guncelleme } = duzenleModal;
    if (!guncelleme.on_ucret_var) {
      guncelleme.on_ucret_turu = null;
      guncelleme.on_ucret_aciklama = null;
      guncelleme.on_ucret_tutari = null;
      guncelleme.on_ucret_para_birimi = null;
    } else {
      guncelleme.on_ucret_tutari = parseFloat(guncelleme.on_ucret_tutari) || null;
      if (guncelleme.on_ucret_turu !== 'Diğer') guncelleme.on_ucret_aciklama = null;
    }
    const { error } = await supabase.from('depo').update(guncelleme).eq('id', id);
    if (error) { alert('Hata: ' + error.message); }
    else {
      if (duzenleModal.sefer_id) {
        await supabase.from('sefer_musteriler')
          .update({ kargo_aciklama: `${duzenleModal.urun} — ${duzenleModal.miktar}` })
          .eq('sefer_id', duzenleModal.sefer_id)
          .eq('musteri_id', duzenleModal.musteri_id);
      }
      if (duzenleModal.durum === 'Teslim Edildi' && duzenleModal.sefer_id) {
        await takipKodlariniGecersizlestir(duzenleModal.sefer_id);
      }
      setDuzenleModal(null);
      mallariGetir();
    }
  };

  const malSil = async (mal) => {
    if (!window.confirm(`"${mal.urun}" kaydını silmek istediğinize emin misiniz?`)) return;
    if (mal.sefer_id && mal.musteri_id) {
      await supabase.from('sefer_musteriler')
        .delete()
        .eq('sefer_id', mal.sefer_id)
        .eq('musteri_id', mal.musteri_id);
    }
    await supabase.from('depo').delete().eq('id', mal.id);
    mallariGetir();
  };

  // ── Seferden Geri Al (Depo sayfasından) ─────────────────────
  const malGeriAlDepo = async (mal) => {
    if (!window.confirm("Bu malı seferden geri almak istediğinize emin misiniz?")) return;
    await supabase.from('depo').update({ sefer_id: null, durum: 'Bekliyor' }).eq('id', mal.id);
    mallariGetir();
    if (secilenSeferIdAtanan) atananMallariGetir(secilenSeferIdAtanan);
  };

  const tiraAta = async () => {
    if (!secilenSeferId) return alert('Sefer seçiniz!');
    const mal = tiraAtaModal;

    // ── Değişiklik 3: Ülke uyumsuzluk kontrolü ──
    const secilenSefer = seferler.find(s => s.id === secilenSeferId);
    if (secilenSefer && mal.hedef_ulke) {
      if (!secilenSefer.guzergah.toLowerCase().includes(mal.hedef_ulke.toLowerCase())) {
        return alert(`Bu mal ${mal.hedef_ulke} için. Seçilen sefer ${secilenSefer.guzergah} güzergahlı. Lütfen doğru seferi seçin.`);
      }
    }

    const musteriId = mal.musteri_id;

    if (musteriId) {
      const { data: mevcutlar } = await supabase
        .from('sefer_musteriler').select('id')
        .eq('sefer_id', secilenSeferId).eq('musteri_id', musteriId);

      if (!mevcutlar || mevcutlar.length === 0) {
        const { error: smError } = await supabase.from('sefer_musteriler').insert([{
          sefer_id: secilenSeferId,
          musteri_id: musteriId,
          kargo_aciklama: kargoAciklama || `${mal.urun} — ${mal.miktar}`
        }]);
        if (smError) return alert('Hata: ' + smError.message);
      }
    }

    await supabase.from('depo').update({
      durum: "TIR'a Atandı", sefer_id: secilenSeferId
    }).eq('id', mal.id);

    setTiraAtaModal(null);
    setSecilenSeferId('');
    setKargoAciklama('');
    mallariGetir();
  };

  const malSec = (id) => {
    setSecilenMallar(prev => {
      const yeni = new Set(prev);
      yeni.has(id) ? yeni.delete(id) : yeni.add(id);
      return yeni;
    });
  };

  const tumunuSec = () => {
    if (secilenMallar.size === filtrelenmisDepoMallar.length) {
      setSecilenMallar(new Set());
    } else {
      setSecilenMallar(new Set(filtrelenmisDepoMallar.map(m => m.id)));
    }
  };

  const topluSil = async () => {
    if (secilenMallar.size === 0) return;
    if (!window.confirm(`Seçili ${secilenMallar.size} mal kalıcı olarak silinecek. Emin misiniz?`)) return;
    for (const malId of secilenMallar) {
      const mal = gorunurMallar.find(m => m.id === malId);
      if (!mal) continue;
      if (mal.sefer_id && mal.musteri_id) {
        await supabase.from('sefer_musteriler')
          .delete()
          .eq('sefer_id', mal.sefer_id)
          .eq('musteri_id', mal.musteri_id);
      }
      await supabase.from('depo').delete().eq('id', malId);
    }
    setSecilenMallar(new Set());
    mallariGetir();
  };

  const topluTiraAta = async () => {
    if (!topluSeferId) return alert('Sefer seçiniz!');
    const secilenSefer = seferler.find(s => s.id === topluSeferId);
    for (const malId of secilenMallar) {
      const mal = gorunurMallar.find(m => m.id === malId);
      if (!mal) continue;
      // Ülke uyumsuzluk kontrolü
      if (secilenSefer && mal.hedef_ulke) {
        if (!secilenSefer.guzergah.toLowerCase().includes(mal.hedef_ulke.toLowerCase())) {
          alert(`"${mal.urun}" (${mal.hedef_ulke}) seçilen seferle uyumsuz — atlanıyor.`);
          continue;
        }
      }
      if (mal.musteri_id) {
        const { data: mevcut } = await supabase
          .from('sefer_musteriler').select('id')
          .eq('sefer_id', topluSeferId).eq('musteri_id', mal.musteri_id);
        if (!mevcut || mevcut.length === 0) {
          await supabase.from('sefer_musteriler').insert([{
            sefer_id: topluSeferId, musteri_id: mal.musteri_id,
            kargo_aciklama: `${mal.urun} — ${mal.miktar}`
          }]);
        }
      }
      await supabase.from('depo').update({ durum: "TIR'a Atandı", sefer_id: topluSeferId }).eq('id', malId);
    }
    setSecilenMallar(new Set());
    setTopluSeferId('');
    mallariGetir();
  };

  const onDepoSiraDegisti = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setDepoSiraliIds(ids => arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id)));
  };

  const onAtananSiraDegisti = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setAtananSiraliIds(ids => arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id)));
  };

  const durumRenk = (durum) => {
    switch(durum) {
      case "TIR'a Atandı": return { bg: '#E6F1FB', renk: '#185FA5' };
      case 'Planlamada': return { bg: '#FEF3C7', renk: '#854F0B' };
      case 'Teslim Edildi': return { bg: '#EAF3DE', renk: '#3B6D11' };
      default: return { bg: '#f1f1f1', renk: '#555' };
    }
  };

  // Kullanıcı bilgileri
  const kullaniciData = kullaniciProp || JSON.parse(localStorage.getItem('kullanici') || '{}');
  const kullaniciAd   = kullaniciData.ad  || '';
  const kullaniciRol  = kullaniciData.rol || '';
  const depoSehir     = kullaniciData.depo_sehir || 'İstanbul';
  const yetkiliRol    = ['yonetici', 'operasyon'].includes(kullaniciRol);

  return (
    <div style={{ flex: 1, background: '#f5f5f5', display: 'flex', flexDirection: 'column', fontFamily: 'Arial, sans-serif', minHeight: '100vh' }}>

      {/* ── Depo rolü header (sidebar yokken gösterilir) ── */}
      {onCikis && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 100,
          background: '#0D3B7A', color: '#fff',
          padding: '0 16px', height: '52px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          flexShrink: 0,
        }}>
          <img src="/logo.png" alt="BYR SWORD" style={{ height: '36px', objectFit: 'contain', borderRadius: '4px' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {kullaniciAd && (
              <span style={{ fontSize: '13px', color: '#a0c4f0', fontWeight: '500' }}>{kullaniciAd}</span>
            )}
            <button
              onClick={onCikis}
              style={{
                background: 'rgba(255,255,255,0.15)', color: '#fff',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '6px', padding: '6px 12px',
                fontSize: '12px', cursor: 'pointer', fontWeight: '600',
              }}
            >
              Çıkış Yap
            </button>
          </div>
        </div>
      )}

      {/* ── Üst bar ── */}
      <div style={{
        background: '#fff', padding: mobil ? '12px 16px' : '14px 24px',
        borderBottom: '1px solid #eee',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px',
      }}>
        <div style={{ fontSize: mobil ? '16px' : '18px', fontWeight: '600' }}>Depo Yönetimi</div>
        {/* Yönetici / Operasyon: şehir filtresi */}
        {yetkiliRol && (
          <select
            value={depoSehirFiltre}
            onChange={e => { setDepoSehirFiltre(e.target.value); mallariGetir(e.target.value); }}
            style={{ padding: '7px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px', color: '#555', background: '#f9f9f9', cursor: 'pointer' }}
          >
            <option value="Tüm Depolar">🏭 Tüm Depolar</option>
            <option value="İstanbul">İstanbul</option>
            <option value="Kayseri">Kayseri</option>
            <option value="Mersin">Mersin</option>
          </select>
        )}
        <button
          onClick={() => setGirisAcik(!girisAcik)}
          style={{
            background: '#3B6D11', color: '#fff', border: 'none',
            padding: mobil ? '8px 14px' : '8px 18px',
            borderRadius: '8px', fontSize: '13px', cursor: 'pointer', fontWeight: '600',
          }}
        >
          {girisAcik ? '✕ Kapat' : '+ Mal Girişi'}
        </button>
      </div>

      <div style={{ padding: mobil ? '12px' : '24px' }}>

        {/* ── Mal Girişi Formu ── */}
        {girisAcik && (
          <div style={{
            background: '#fff', borderRadius: '10px', border: '1px solid #ddd',
            padding: mobil ? '16px' : '20px', marginBottom: '16px',
          }}>
            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '16px', color: '#0D3B7A' }}>Yeni Mal Girişi</div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: mobil ? '1fr' : 'repeat(3, 1fr)',
              gap: '12px', marginBottom: '12px',
            }}>

              {/* Müşteri — aranabilir + eklenebilir */}
              <div ref={musteriDropRef} style={{ position: 'relative' }}>
                <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                  Müşteri
                  <button onClick={() => setYeniMusteriModal(true)}
                    style={{ marginLeft: '8px', background: '#0D3B7A', color: '#fff', border: 'none', borderRadius: '4px', padding: '2px 7px', fontSize: '11px', cursor: 'pointer' }}>
                    + Yeni
                  </button>
                </div>
                <input
                  value={musteriArama}
                  onChange={e => { setMusteriArama(e.target.value); setMusteriDropAcik(true); setYeniMal({ ...yeniMal, musteri_id: '', musteri_ad: '' }); }}
                  onFocus={() => setMusteriDropAcik(true)}
                  placeholder="Müşteri ara veya seç..."
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                />
                {musteriDropAcik && filtrelenmisMusteriler.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ddd', borderRadius: '6px', zIndex: 100, maxHeight: '180px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                    {filtrelenmisMusteriler.map(m => (
                      <div key={m.id} onClick={() => musteriSec(m)}
                        style={{ padding: '10px 12px', cursor: 'pointer', fontSize: '14px', borderBottom: '1px solid #f0f0f0' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                        {m.ad} <span style={{ fontSize: '11px', color: '#888' }}>({m.tip})</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Diğer alanlar */}
              {[
                { label: 'Ürün / Açıklama *', key: 'urun', placeholder: 'Ev Eşyası, Tekstil...' },
                { label: 'Miktar', key: 'miktar', placeholder: '12 koli, 3 palet' },
                { label: 'Fatura No', key: 'fatura_no', placeholder: 'BYR2026000045' },
                { label: 'Gönderici Firma', key: 'gonderici_firma', placeholder: 'Firma adı veya kişi...' },
                { label: 'Not', key: 'notlar', placeholder: 'Kırılgan, soğuk zincir...' },
              ].map(f => (
                <div key={f.key}>
                  <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>{f.label}</div>
                  <input value={yeniMal[f.key]} onChange={e => setYeniMal({ ...yeniMal, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
              ))}

              {/* Hedef Ülke */}
              <div>
                <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>Hedef Ülke</div>
                <select value={yeniMal.hedef_ulke} onChange={e => setYeniMal({ ...yeniMal, hedef_ulke: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}>
                  {ULKELER.map((u, i) => u === '---'
                    ? <option key={i} disabled>──────────────</option>
                    : <option key={i} value={u}>{u}</option>
                  )}
                </select>
              </div>
            </div>
            {/* Ön Ücret */}
            <div style={{ marginTop: '12px', padding: '12px 14px', background: '#fff8f0', border: '1px solid #f0d9bb', borderRadius: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', color: '#854F0B' }}>
                <input type="checkbox" checked={yeniMal.on_ucret_var}
                  onChange={e => setYeniMal({ ...yeniMal, on_ucret_var: e.target.checked })} />
                💰 Ön Ücret Var
              </label>
              {yeniMal.on_ucret_var && (
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1', minWidth: '150px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '5px' }}>Ücret Türü</div>
                    <select value={yeniMal.on_ucret_turu}
                      onChange={e => setYeniMal({ ...yeniMal, on_ucret_turu: e.target.value })}
                      style={{ width: '100%', padding: '9px', border: '1px solid #e0c8aa', borderRadius: '6px', fontSize: '14px', background: '#fff' }}>
                      <option>İç Nakliye</option>
                      <option>Kargo Ücreti</option>
                      <option>Depolama</option>
                      <option>Diğer</option>
                    </select>
                  </div>
                  {yeniMal.on_ucret_turu === 'Diğer' && (
                    <div style={{ flex: '1', minWidth: '140px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '5px' }}>Açıklama</div>
                      <input value={yeniMal.on_ucret_aciklama}
                        onChange={e => setYeniMal({ ...yeniMal, on_ucret_aciklama: e.target.value })}
                        placeholder="Ücret açıklaması..."
                        style={{ width: '100%', padding: '9px', border: '1px solid #e0c8aa', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', background: '#fff' }} />
                    </div>
                  )}
                  <div style={{ flex: '1', minWidth: '110px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '5px' }}>Tutar</div>
                    <input type="number" value={yeniMal.on_ucret_tutari}
                      onChange={e => setYeniMal({ ...yeniMal, on_ucret_tutari: e.target.value })}
                      placeholder="0"
                      style={{ width: '100%', padding: '9px', border: '1px solid #e0c8aa', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', background: '#fff' }} />
                  </div>
                  <div style={{ flex: '0 0 auto', minWidth: '100px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '5px' }}>Para Birimi</div>
                    <select value={yeniMal.on_ucret_para_birimi}
                      onChange={e => setYeniMal({ ...yeniMal, on_ucret_para_birimi: e.target.value })}
                      style={{ width: '100%', padding: '9px', border: '1px solid #e0c8aa', borderRadius: '6px', fontSize: '14px', background: '#fff' }}>
                      <option value="TL">₺ TL</option>
                      <option value="USD">$ USD</option>
                      <option value="EUR">€ EUR</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '12px' }}>
              <button onClick={() => { setGirisAcik(false); setMusteriArama(''); }}
                style={{ padding: '10px 18px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '14px' }}>
                Vazgeç
              </button>
              <button onClick={malEkle}
                style={{ padding: '10px 20px', background: '#3B6D11', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
                Depoya Kaydet
              </button>
            </div>
          </div>
        )}

        {/* ── Metrikler — mobilde 2x2, masaüstünde 4'lü ── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: mobil ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: mobil ? '10px' : '16px',
          marginBottom: mobil ? '14px' : '20px',
        }}>
          {[
            { label: 'Depodaki Mal', value: gorunurMallar.length, renk: '#0D3B7A' },
            { label: 'Bekleyen', value: mallar.filter(m => m.durum === 'Bekliyor').length, renk: '#854F0B' },
            { label: "Sefere Atanan", value: mallar.filter(m => m.durum === "TIR'a Atandı").length, renk: '#185FA5' },
            { label: 'Teslim Edilen', value: mallar.filter(m => m.durum === 'Teslim Edildi').length, renk: '#3B6D11' },
          ].map((card, i) => (
            <div key={i} style={{ background: '#fff', borderRadius: '10px', padding: mobil ? '12px' : '16px', border: '1px solid #eee' }}>
              <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>{card.label}</div>
              <div style={{ fontSize: mobil ? '26px' : '28px', fontWeight: '700', color: card.renk }}>{card.value}</div>
            </div>
          ))}
        </div>

        {/* ── Sekme Butonları ── */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
          <button
            onClick={() => setAktifDepoSekme('depo')}
            style={{
              padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer',
              fontSize: '13px', fontWeight: aktifDepoSekme === 'depo' ? '600' : '400',
              background: aktifDepoSekme === 'depo' ? '#0D3B7A' : '#f1f1f1',
              color: aktifDepoSekme === 'depo' ? '#fff' : '#555',
            }}
          >
            📦 Depodaki Mallar ({gorunurMallar.length})
          </button>
          <button
            onClick={() => setAktifDepoSekme('atanan')}
            style={{
              padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer',
              fontSize: '13px', fontWeight: aktifDepoSekme === 'atanan' ? '600' : '400',
              background: aktifDepoSekme === 'atanan' ? '#185FA5' : '#f1f1f1',
              color: aktifDepoSekme === 'atanan' ? '#fff' : '#555',
            }}
          >
            🚛 Sefere Atanan ({atananMallar.length})
          </button>
          {yetkiliRol && (
            <button
              onClick={depoListesiPDF}
              style={{ marginLeft: 'auto', padding: '7px 14px', borderRadius: '8px', border: '1px solid #0D3B7A', background: '#fff', color: '#0D3B7A', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
            >
              📄 PDF İndir
            </button>
          )}
        </div>

        {/* ── Arama Kutuları ── */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
          {[
            { value: aramaMusteri, set: setAramaMusteri, placeholder: 'Müşteri Ara...' },
            { value: aramaFaturaNo, set: setAramaFaturaNo, placeholder: 'Fatura No Ara...' },
            { value: aramaGonderici, set: setAramaGonderici, placeholder: 'Gönderici Ara...' },
          ].map(({ value, set, placeholder }) => (
            <div key={placeholder} style={{ position: 'relative', flex: '1', minWidth: '140px' }}>
              <input
                value={value}
                onChange={e => set(e.target.value)}
                placeholder={placeholder}
                style={{ width: '100%', padding: '7px 28px 7px 10px', border: '1px solid #dde', borderRadius: '7px', fontSize: '13px', color: '#333', background: '#fafbff', boxSizing: 'border-box' }}
              />
              {value && (
                <button
                  onClick={() => set('')}
                  style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: '14px', lineHeight: 1, padding: '0 2px' }}
                >✕</button>
              )}
            </div>
          ))}
        </div>

        {/* ── Depodaki Mallar Listesi ── */}
        {aktifDepoSekme === 'depo' && (
          <div style={{ background: '#fff', borderRadius: '10px', border: '1px solid #eee', overflow: 'hidden' }}>
            {/* Başlık */}
            <div style={{
              padding: mobil ? '12px 14px' : '12px 20px',
              borderBottom: '1px solid #eee',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              flexWrap: 'wrap', gap: '8px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <div style={{ fontSize: '14px', fontWeight: '600' }}>Depodaki Mallar ({gorunurMallar.length})</div>
                {mevcutUlkeler.length > 1 && (
                  <select
                    value={ulkeFiltre}
                    onChange={e => setUlkeFiltre(e.target.value)}
                    style={{ padding: '5px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '12px', color: '#555' }}
                  >
                    <option value="Tümü">Tüm Ülkeler</option>
                    {mevcutUlkeler.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                )}
              </div>
              {secilenMallar.size > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13px', color: '#0D3B7A', fontWeight: '600' }}>{secilenMallar.size} mal seçildi</span>
                  <select value={topluSeferId} onChange={e => setTopluSeferId(e.target.value)}
                    style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '12px', minWidth: '150px' }}>
                    <option value=''>-- Sefer seçin --</option>
                    {seferler.map(s => (
                      <option key={s.id} value={s.id}>{s.sefer_no} · {s.guzergah}</option>
                    ))}
                  </select>
                  <button onClick={topluTiraAta}
                    style={{ background: '#0D3B7A', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>
                    Sefere Ata
                  </button>
                  <button onClick={topluSil}
                    style={{ background: '#cc0000', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>
                    🗑️ Sil ({secilenMallar.size})
                  </button>
                  <button onClick={() => setSecilenMallar(new Set())}
                    style={{ background: 'none', border: '1px solid #ddd', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', color: '#888' }}>
                    İptal
                  </button>
                </div>
              )}
            </div>

            {yukleniyor ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>Yükleniyor...</div>
            ) : filtrelenmisDepoMallar.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>
                {gorunurMallar.length === 0 ? 'Depoda bekleyen mal yok.' : 'Arama kriterlerine uygun kayıt bulunamadı.'}
              </div>
            ) : mobil ? (
              /* ── MOBİL: Kart görünümü ── */
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px' }}>
                {filtrelenmisDepoMallar.map(row => {
                  const { bg, renk } = durumRenk(row.durum);
                  return (
                    <div key={row.id} style={{
                      background: secilenMallar.has(row.id) ? '#f0f4ff' : '#fafafa',
                      border: `1px solid ${secilenMallar.has(row.id) ? '#b0c4f0' : '#e8e8e8'}`,
                      borderRadius: '10px', padding: '14px',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '15px', fontWeight: '700', color: '#1a1a1a', marginBottom: '2px' }}>
                            {row.musteriler?.ad || '—'}
                          </div>
                          <div style={{ fontSize: '13px', color: '#444' }}>{row.urun}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, marginLeft: '8px' }}>
                          <input
                            type='checkbox'
                            checked={secilenMallar.has(row.id)}
                            onChange={() => malSec(row.id)}
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                          />
                          <span style={{ background: bg, color: renk, padding: '3px 9px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' }}>
                            {row.durum}
                          </span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                        {row.miktar && (
                          <span style={{ fontSize: '12px', color: '#555' }}>📦 {row.miktar}</span>
                        )}
                        <span style={{ background: '#E3F0FF', color: '#0D3B7A', padding: '1px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>
                          {row.hedef_ulke}
                        </span>
                        {row.fatura_no && (
                          <span style={{ fontSize: '11px', color: '#888' }}>{row.fatura_no}</span>
                        )}
                        {row.irsaliye_no && (
                          <span style={{ fontSize: '11px', color: '#888' }}>İrs: {row.irsaliye_no}</span>
                        )}
                      </div>
                      {row.takip_kodu && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '10px' }}>
                          <span style={{
                            fontFamily: 'monospace', fontSize: '12px', fontWeight: '700',
                            color: row.kod_gecerli === false ? '#aaa' : '#0D3B7A',
                            background: row.kod_gecerli === false ? '#f5f5f5' : '#EFF6FF',
                            padding: '3px 8px', borderRadius: '5px',
                            textDecoration: row.kod_gecerli === false ? 'line-through' : 'none',
                          }}>
                            {row.takip_kodu}
                          </span>
                          <button
                            onClick={() => koduKopyala(row.takip_kodu)}
                            title="Kopyala"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', padding: '2px' }}
                          >
                            {kopyalandi === row.takip_kodu ? '✅' : '📋'}
                          </button>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <button onClick={() => setDuzenleModal({ ...row })}
                          style={{ background: '#f0f4ff', color: '#0D3B7A', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>
                          ✏️ Düzenle
                        </button>
                        <button onClick={() => malSil(row)}
                          style={{ background: '#fff0f0', color: '#cc0000', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>
                          🗑️ Sil
                        </button>
                        {row.barkod_no && (
                          <button onClick={() => etiketIndir(row)}
                            style={{ background: '#F0FDF4', color: '#3B6D11', border: '1px solid #c6e6a3', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>
                            🏷️ Etiket
                          </button>
                        )}
                        {row.durum !== "TIR'a Atandı" && row.durum !== 'Teslim Edildi' && (
                          <button onClick={() => { setTiraAtaModal(row); setKargoAciklama(`${row.urun} — ${row.miktar}`); }}
                            style={{ background: '#0D3B7A', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>
                            Sefere Ata
                          </button>
                        )}
                        {row.durum === "TIR'a Atandı" && (
                          <span style={{ fontSize: '12px', color: '#185FA5', fontWeight: '600', padding: '6px 0' }}>✓ Sefere Atandı</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* ── MASAÜSTÜ: Tablo görünümü ── */
              <div style={{ overflowX: 'auto' }}>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDepoSiraDegisti}>
              <SortableContext items={depoSayfaliIds} strategy={verticalListSortingStrategy}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '900px' }}>
                <thead>
                  <tr style={{ background: '#f9f9f9' }}>
                    <th style={{ padding: '10px 8px', width: '28px' }}></th>
                    <th style={{ padding: '10px 12px', width: '36px' }}>
                      <input type='checkbox'
                        checked={secilenMallar.size === filtrelenmisDepoMallar.length && filtrelenmisDepoMallar.length > 0}
                        onChange={tumunuSec}
                        style={{ cursor: 'pointer', width: '15px', height: '15px' }} />
                    </th>
                    {['Müşteri', 'Ürün', 'Miktar', 'Hedef', 'Fatura No', 'Gönderici Firma', 'Kayıt Tarihi', ...(yetkiliRol ? ['Depo'] : []), 'Durum', 'İşlem'].map((h, i) => (
                      <th key={i} style={{ padding: '10px 16px', textAlign: 'left', color: '#888', fontWeight: '500', fontSize: '12px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {depoSayfaliIds.map(id => gorunurMallar.find(m => m.id === id)).filter(Boolean).map((row) => {
                    const { bg, renk } = durumRenk(row.durum);
                    return (
                      <React.Fragment key={row.id}>
                      <SortableRow id={row.id}>
                      {(listeners) => (<>
                        <td style={{ padding: '11px 8px', cursor: 'grab', color: '#bbb', textAlign: 'center', userSelect: 'none' }} {...listeners}>⠿</td>
                        <td style={{ padding: '11px 12px' }}>
                          <input type='checkbox' checked={secilenMallar.has(row.id)} onChange={() => malSec(row.id)}
                            style={{ cursor: 'pointer', width: '15px', height: '15px' }} />
                        </td>
                        <td style={{ padding: '11px 16px', fontWeight: '500' }}>
                          {row.musteriler?.ad || '—'}
                          {row.on_ucret_var && (
                            <span
                              title={`${row.on_ucret_turu}${row.on_ucret_turu === 'Diğer' && row.on_ucret_aciklama ? ' — ' + row.on_ucret_aciklama : ''}: ${row.on_ucret_tutari ?? '?'} ${row.on_ucret_para_birimi || 'TL'}`}
                              style={{ marginLeft: '6px', cursor: 'help', fontSize: '13px' }}>💰</span>
                          )}
                        </td>
                        <td style={{ padding: '11px 16px' }}>{row.urun}</td>
                        <td style={{ padding: '11px 16px', color: '#888' }}>{row.miktar}</td>
                        <td style={{ padding: '11px 16px' }}>
                          <span style={{ background: '#E3F0FF', color: '#0D3B7A', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>{row.hedef_ulke}</span>
                        </td>
                        <td style={{ padding: '11px 16px', color: '#888', fontSize: '12px' }}>{row.fatura_no || '—'}</td>
                        <td style={{ padding: '11px 16px', color: '#888', fontSize: '12px' }}>{row.gonderici_firma || '—'}</td>
                        <td style={{ padding: '11px 16px', color: '#888', fontSize: '11px', whiteSpace: 'nowrap' }}>
                          {row.created_at ? new Date(row.created_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </td>
                        {yetkiliRol && (
                          <td style={{ padding: '11px 16px', color: '#555', fontSize: '12px', whiteSpace: 'nowrap' }}>
                            {row.depo_sehir || '—'}
                          </td>
                        )}
                        <td style={{ padding: '11px 16px' }}>
                          <span style={{ background: bg, color: renk, padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' }}>{row.durum}</span>
                        </td>
                        <td style={{ padding: '11px 16px' }}>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'nowrap' }}>
                            <button onClick={() => setDuzenleModal({ ...row })}
                              style={{ background: '#f0f4ff', color: '#0D3B7A', border: 'none', padding: '4px 8px', borderRadius: '5px', fontSize: '11px', cursor: 'pointer', fontWeight: '600' }}>
                              ✏️
                            </button>
                            <button onClick={() => malSil(row)}
                              style={{ background: '#fff0f0', color: '#cc0000', border: 'none', padding: '4px 8px', borderRadius: '5px', fontSize: '11px', cursor: 'pointer', fontWeight: '600' }}>
                              🗑️
                            </button>
                            {(row.takip_kodu || (yetkiliRol && row.teslim_kodu)) && (
                              <button
                                onClick={() => setAcikKodSatiri(prev => prev === row.id ? null : row.id)}
                                style={{ background: acikKodSatiri === row.id ? '#EFF6FF' : '#f5f5f5', color: '#0D3B7A', border: '1px solid #c8ddf5', padding: '4px 8px', borderRadius: '5px', fontSize: '11px', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                🔍 Kod
                              </button>
                            )}
                            {row.barkod_no && (
                              <button onClick={() => etiketIndir(row)}
                                style={{ background: '#F0FDF4', color: '#3B6D11', border: '1px solid #c6e6a3', padding: '4px 8px', borderRadius: '5px', fontSize: '11px', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                🏷️ Etiket
                              </button>
                            )}
                            {row.durum !== "TIR'a Atandı" && row.durum !== 'Teslim Edildi' && (
                              <button onClick={() => { setTiraAtaModal(row); setKargoAciklama(`${row.urun} — ${row.miktar}`); }}
                                style={{ background: '#0D3B7A', color: '#fff', border: 'none', padding: '5px 10px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                Sefere Ata
                              </button>
                            )}
                            {row.durum === "TIR'a Atandı" && (
                              <span style={{ fontSize: '11px', color: '#185FA5' }}>✓ Atandı</span>
                            )}
                          </div>
                        </td>
                      </>)}
                      </SortableRow>
                      {acikKodSatiri === row.id && (
                        <tr>
                          <td colSpan={99} style={{ padding: '0 16px 10px 60px', background: '#F8FBFF' }}>
                            <div style={{ display: 'inline-flex', flexDirection: 'column', gap: '6px', background: '#fff', border: '1px solid #c8ddf5', borderRadius: '8px', padding: '10px 14px', fontSize: '12px' }}>
                              {row.takip_kodu && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ color: '#888', minWidth: '80px' }}>Takip Kodu:</span>
                                  <span style={{ fontFamily: 'monospace', fontWeight: '700', color: row.kod_gecerli === false ? '#aaa' : '#0D3B7A', textDecoration: row.kod_gecerli === false ? 'line-through' : 'none' }}>{row.takip_kodu}</span>
                                  <button onClick={() => koduKopyala(row.takip_kodu)} title="Kopyala" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', padding: 0, lineHeight: 1 }}>
                                    {kopyalandi === row.takip_kodu ? '✅' : '📋'}
                                  </button>
                                </div>
                              )}
                              {yetkiliRol && row.teslim_kodu && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ color: '#888', minWidth: '80px' }}>Teslim Kodu:</span>
                                  <span style={{ fontFamily: 'monospace', fontWeight: '700', color: '#854F0B', background: '#FEF3C7', padding: '2px 7px', borderRadius: '4px' }}>{row.teslim_kodu}</span>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              </SortableContext>
              </DndContext>
              </div>
            )}

            <PaginasyonBar
              toplamKayit={depoToplamKayit}
              sayfaNo={depoSayfaNo}
              toplamSayfa={depoToplamSayfa}
              sayfaBasinaKayit={depoSayfaBasinaKayit}
              onSayfaBasinaKayitDegis={(v) => { setDepoSayfaBasinaKayit(v); setDepoSayfaNo(1); }}
              onSayfaDegis={setDepoSayfaNo}
              solBilgi={<span>KKTC: <strong>{depoMallar.filter(m => m.hedef_ulke === 'KKTC').length}</strong> · Montenegro: <strong>{depoMallar.filter(m => m.hedef_ulke === 'Montenegro').length}</strong>{ulkeFiltre !== 'Tümü' && <span style={{ color: '#185FA5' }}> · {ulkeFiltre}</span>}</span>}
              mobil={mobil}
            />
          </div>
        )}

        {/* ── Sefere Atanan Mallar ── */}
        {aktifDepoSekme === 'atanan' && (
          <div style={{ background: '#fff', borderRadius: '10px', border: '1px solid #eee', overflow: 'hidden' }}>
            {/* Başlık + sefer filtresi */}
            <div style={{ padding: mobil ? '12px 14px' : '12px 20px', borderBottom: '1px solid #eee' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: '14px', fontWeight: '600' }}>Sefere Atanan Mallar</div>
                <select
                  value={secilenSeferIdAtanan}
                  onChange={e => {
                    const sid = e.target.value;
                    setSecilenSeferIdAtanan(sid);
                    atananMallariGetir(sid);
                  }}
                  style={{ border: '1px solid #dde', borderRadius: '6px', padding: '6px 10px', fontSize: '13px', color: '#333', background: '#f9faff', minWidth: '220px' }}
                >
                  <option value="">— Sefer seçin —</option>
                  {aktifSeferlerAtanan.map(s => (
                    <option key={s.id} value={s.id}>{s.sefer_no} · {s.guzergah} · {s.durum}</option>
                  ))}
                </select>
              </div>
              {/* Seçili sefer istatistikleri */}
              {secilenSeferIdAtanan && !atananYukleniyor && atananSeferMallari.length > 0 && (() => {
                const sf = aktifSeferlerAtanan.find(s => s.id === secilenSeferIdAtanan);
                return (
                  <div style={{ marginTop: '10px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '8px', padding: '8px 14px', fontSize: '13px', color: '#1D4ED8', fontWeight: '600' }}>
                    {sf?.sefer_no} | {atananSeferMallari.length} kalem
                  </div>
                );
              })()}
            </div>

            {!secilenSeferIdAtanan ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#aaa', fontSize: '14px' }}>Listelemek için bir sefer seçin.</div>
            ) : atananYukleniyor ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>Yükleniyor...</div>
            ) : filtrelenmisAtananMallar.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>
                {atananSeferMallari.length === 0 ? 'Bu sefere atanmış mal yok.' : 'Arama kriterlerine uygun kayıt bulunamadı.'}
              </div>
            ) : mobil ? (
              /* ── MOBİL: Kart görünümü ── */
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px' }}>
                {atananSayfaliIds.map(id => atananSeferMallari.find(m => m.id === id)).filter(Boolean).map(row => (
                  <div key={row.id} style={{ background: '#f0f6ff', border: '1px solid #c8ddf5', borderRadius: '10px', padding: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <div>
                        <div style={{ fontSize: '15px', fontWeight: '700', color: '#1a1a1a', marginBottom: '2px' }}>{row.musteriler?.ad || '—'}</div>
                        <div style={{ fontSize: '13px', color: '#444' }}>{row.urun}</div>
                      </div>
                      <span style={{ background: '#E6F1FB', color: '#185FA5', padding: '3px 9px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' }}>Sefere Atandı</span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                      {row.miktar && <span style={{ fontSize: '12px', color: '#555' }}>📦 {row.miktar}</span>}
                      <span style={{ background: '#E3F0FF', color: '#0D3B7A', padding: '1px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>{row.hedef_ulke}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={() => setDuzenleModal({ ...row })}
                        style={{ background: '#f0f4ff', color: '#0D3B7A', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>
                        ✏️ Düzenle
                      </button>
                      <button onClick={() => malGeriAlDepo(row)}
                        style={{ background: '#fff0f0', color: '#cc0000', border: '1px solid #fcc', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>
                        ↩ Geri Al
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* ── MASAÜSTÜ: Tablo + DnD ── */
              <div style={{ overflowX: 'auto' }}>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onAtananSiraDegisti}>
              <SortableContext items={atananSayfaliIds} strategy={verticalListSortingStrategy}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '800px' }}>
                <thead>
                  <tr style={{ background: '#f9f9f9' }}>
                    <th style={{ padding: '10px 8px', width: '28px' }}></th>
                    {['Müşteri', 'Ürün', 'Miktar', 'Hedef', 'Gönderici Firma', 'Kayıt Tarihi', 'İşlem'].map((h, i) => (
                      <th key={i} style={{ padding: '10px 16px', textAlign: 'left', color: '#888', fontWeight: '500', fontSize: '12px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {atananSayfaliIds.map(id => atananSeferMallari.find(m => m.id === id)).filter(Boolean).map((row) => (
                    <SortableRow key={row.id} id={row.id}>
                    {(listeners) => (<>
                      <td style={{ padding: '11px 8px', cursor: 'grab', color: '#bbb', textAlign: 'center', userSelect: 'none' }} {...listeners}>⠿</td>
                      <td style={{ padding: '11px 16px', fontWeight: '500' }}>{row.musteriler?.ad || '—'}</td>
                      <td style={{ padding: '11px 16px' }}>{row.urun}</td>
                      <td style={{ padding: '11px 16px', color: '#888' }}>{row.miktar || '—'}</td>
                      <td style={{ padding: '11px 16px' }}>
                        <span style={{ background: '#E3F0FF', color: '#0D3B7A', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>{row.hedef_ulke}</span>
                      </td>
                      <td style={{ padding: '11px 16px', color: '#888', fontSize: '12px' }}>{row.gonderici_firma || '—'}</td>
                      <td style={{ padding: '11px 16px', color: '#888', fontSize: '11px', whiteSpace: 'nowrap' }}>
                        {row.created_at ? new Date(row.created_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                      <td style={{ padding: '11px 16px' }}>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <button onClick={() => setDuzenleModal({ ...row })}
                            style={{ background: '#f0f4ff', color: '#0D3B7A', border: 'none', padding: '4px 8px', borderRadius: '5px', fontSize: '11px', cursor: 'pointer', fontWeight: '600' }}>
                            ✏️
                          </button>
                          <button onClick={() => malGeriAlDepo(row)}
                            style={{ background: '#fff0f0', color: '#cc0000', border: '1px solid #fcc', padding: '4px 10px', borderRadius: '5px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>
                            ↩ Geri Al
                          </button>
                        </div>
                      </td>
                    </>)}
                    </SortableRow>
                  ))}
                </tbody>
              </table>
              </SortableContext>
              </DndContext>
              </div>
            )}

            {secilenSeferIdAtanan && !atananYukleniyor && atananSeferMallari.length > 0 && (
              <PaginasyonBar
                toplamKayit={atananToplamKayit}
                sayfaNo={atananSayfaNo}
                toplamSayfa={atananToplamSayfa}
                sayfaBasinaKayit={atananSayfaBasinaKayit}
                onSayfaBasinaKayitDegis={(v) => { setAtananSayfaBasinaKayit(v); setAtananSayfaNo(1); }}
                onSayfaDegis={setAtananSayfaNo}
                mobil={mobil}
              />
            )}
          </div>
        )}

      </div>

      {/* ── Yeni Müşteri Modal ── */}
      {yeniMusteriModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '440px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <div style={{ fontSize: '16px', fontWeight: '600' }}>Yeni Müşteri Ekle</div>
              <button onClick={() => setYeniMusteriModal(false)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#888' }}>✕</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: mobil ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>Ad Soyad / Firma Adı *</div>
                <input value={yeniMusteri.ad} onChange={e => setYeniMusteri({ ...yeniMusteri, ad: e.target.value })}
                  placeholder="Mehmet Yıldırım veya Karadeniz Koop."
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>Tip</div>
                <select value={yeniMusteri.tip} onChange={e => setYeniMusteri({ ...yeniMusteri, tip: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}>
                  <option>Bireysel</option>
                  <option>Kurumsal</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>Telefon</div>
                <input value={yeniMusteri.telefon} onChange={e => setYeniMusteri({ ...yeniMusteri, telefon: formatTelefon(e.target.value) })}
                  placeholder="0532 123 45 67"
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>E-posta</div>
                <input value={yeniMusteri.email} onChange={e => setYeniMusteri({ ...yeniMusteri, email: e.target.value })}
                  placeholder="ornek@mail.com"
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>Şehir</div>
                <input value={yeniMusteri.sehir} onChange={e => setYeniMusteri({ ...yeniMusteri, sehir: e.target.value })}
                  placeholder="Lefkoşa, Podgorica..."
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setYeniMusteriModal(false)} style={{ padding: '10px 18px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff', cursor: 'pointer' }}>İptal</button>
              <button onClick={yeniMusteriKaydet} style={{ padding: '10px 20px', background: '#0D3B7A', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Düzenle Modal ── */}
      {duzenleModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <div style={{ fontSize: '16px', fontWeight: '600' }}>Mal Düzenle</div>
              <button onClick={() => setDuzenleModal(null)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#888' }}>✕</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: mobil ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              {[
                { label: 'Ürün / Açıklama *', key: 'urun', placeholder: 'Ev Eşyası...' },
                { label: 'Miktar', key: 'miktar', placeholder: '12 koli' },
                { label: 'Fatura No', key: 'fatura_no', placeholder: 'BYR2026000045' },
                { label: 'Gönderici Firma', key: 'gonderici_firma', placeholder: 'Firma adı veya kişi...' },
                { label: 'Not', key: 'notlar', placeholder: 'Kırılgan...' },
              ].map(f => (
                <div key={f.key}>
                  <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>{f.label}</div>
                  <input value={duzenleModal[f.key] || ''} onChange={e => setDuzenleModal({ ...duzenleModal, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
              ))}
              <div>
                <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>Hedef Ülke</div>
                <select value={duzenleModal.hedef_ulke || 'KKTC'} onChange={e => setDuzenleModal({ ...duzenleModal, hedef_ulke: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}>
                  {ULKELER.map((u, i) => u === '---'
                    ? <option key={i} disabled>──────────────</option>
                    : <option key={i} value={u}>{u}</option>
                  )}
                </select>
              </div>
              <div>
                <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>Durum</div>
                <select value={duzenleModal.durum} onChange={e => setDuzenleModal({ ...duzenleModal, durum: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}>
                  <option>Bekliyor</option>
                  <option>Planlamada</option>
                  <option>TIR'a Atandı</option>
                  <option>Teslim Edildi</option>
                </select>
              </div>
            </div>
            {/* Ön Ücret */}
            <div style={{ marginBottom: '16px', padding: '12px 14px', background: '#fff8f0', border: '1px solid #f0d9bb', borderRadius: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', color: '#854F0B' }}>
                <input type="checkbox" checked={!!duzenleModal.on_ucret_var}
                  onChange={e => setDuzenleModal({ ...duzenleModal, on_ucret_var: e.target.checked })} />
                💰 Ön Ücret Var
              </label>
              {duzenleModal.on_ucret_var && (
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1', minWidth: '140px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '5px' }}>Ücret Türü</div>
                    <select value={duzenleModal.on_ucret_turu || 'İç Nakliye'}
                      onChange={e => setDuzenleModal({ ...duzenleModal, on_ucret_turu: e.target.value })}
                      style={{ width: '100%', padding: '9px', border: '1px solid #e0c8aa', borderRadius: '6px', fontSize: '14px', background: '#fff' }}>
                      <option>İç Nakliye</option>
                      <option>Kargo Ücreti</option>
                      <option>Depolama</option>
                      <option>Diğer</option>
                    </select>
                  </div>
                  {duzenleModal.on_ucret_turu === 'Diğer' && (
                    <div style={{ flex: '1', minWidth: '130px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '5px' }}>Açıklama</div>
                      <input value={duzenleModal.on_ucret_aciklama || ''}
                        onChange={e => setDuzenleModal({ ...duzenleModal, on_ucret_aciklama: e.target.value })}
                        placeholder="Açıklama..."
                        style={{ width: '100%', padding: '9px', border: '1px solid #e0c8aa', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', background: '#fff' }} />
                    </div>
                  )}
                  <div style={{ flex: '1', minWidth: '100px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '5px' }}>Tutar</div>
                    <input type="number" value={duzenleModal.on_ucret_tutari ?? ''}
                      onChange={e => setDuzenleModal({ ...duzenleModal, on_ucret_tutari: e.target.value })}
                      placeholder="0"
                      style={{ width: '100%', padding: '9px', border: '1px solid #e0c8aa', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', background: '#fff' }} />
                  </div>
                  <div style={{ flex: '0 0 auto', minWidth: '95px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '5px' }}>Para Birimi</div>
                    <select value={duzenleModal.on_ucret_para_birimi || 'TL'}
                      onChange={e => setDuzenleModal({ ...duzenleModal, on_ucret_para_birimi: e.target.value })}
                      style={{ width: '100%', padding: '9px', border: '1px solid #e0c8aa', borderRadius: '6px', fontSize: '14px', background: '#fff' }}>
                      <option value="TL">₺ TL</option>
                      <option value="USD">$ USD</option>
                      <option value="EUR">€ EUR</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setDuzenleModal(null)} style={{ padding: '10px 18px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff', cursor: 'pointer' }}>İptal</button>
              <button onClick={malGuncelle} style={{ padding: '10px 20px', background: '#0D3B7A', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>Güncelle</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sefere Ata Modal ── */}
      {tiraAtaModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '420px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <div>
                <div style={{ fontSize: '16px', fontWeight: '600' }}>Sefere Ata</div>
                <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                  {tiraAtaModal.musteriler?.ad} · {tiraAtaModal.urun}
                </div>
                {tiraAtaModal.hedef_ulke && (
                  <div style={{ marginTop: '4px' }}>
                    <span style={{ background: '#E3F0FF', color: '#0D3B7A', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>
                      Hedef: {tiraAtaModal.hedef_ulke}
                    </span>
                  </div>
                )}
              </div>
              <button onClick={() => setTiraAtaModal(null)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#888' }}>✕</button>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Sefer Seç *</div>
              <select value={secilenSeferId} onChange={e => setSecilenSeferId(e.target.value)}
                style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}>
                <option value="">-- Sefer seçin --</option>
                {seferler.map(s => (
                  <option key={s.id} value={s.id}>{s.sefer_no} · {s.guzergah} · {s.durum}</option>
                ))}
              </select>
              {/* Uyumsuzluk uyarısı */}
              {secilenSeferId && tiraAtaModal.hedef_ulke && (() => {
                const secilen = seferler.find(s => s.id === secilenSeferId);
                if (secilen && !secilen.guzergah.toLowerCase().includes(tiraAtaModal.hedef_ulke.toLowerCase())) {
                  return (
                    <div style={{ marginTop: '8px', padding: '8px 12px', background: '#FFF3CD', border: '1px solid #FFDDA0', borderRadius: '6px', fontSize: '12px', color: '#854F0B' }}>
                      ⚠️ Uyarı: Bu sefer <strong>{secilen.guzergah}</strong> güzergahlı, mal ise <strong>{tiraAtaModal.hedef_ulke}</strong> için. Lütfen doğru seferi seçin.
                    </div>
                  );
                }
                return null;
              })()}
            </div>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Kargo Açıklaması</div>
              <input value={kargoAciklama} onChange={e => setKargoAciklama(e.target.value)}
                style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setTiraAtaModal(null)} style={{ padding: '10px 18px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff', cursor: 'pointer' }}>İptal</button>
              <button onClick={tiraAta} style={{ padding: '10px 20px', background: '#0D3B7A', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>Ata</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Depo;
